import { endAlignment } from "./contactForm";
import { useEffect, useState } from "react";
import {
  ProjectFormErrors,
  ProjectTouchedFields,
  ProjectDataProps,
} from "../../common/types";
import LinksInputComp from "./FormComponents/LinksInputComp";
import FilesInputComp from "./FormComponents/FilesInputComp";
import { v4 as uuidv4 } from "uuid";
import CombinedInput from "./FormComponents/InputComp";
import { styled } from "styled-components";
import ProjectView from "../../pages/ProjectView";
import { Squarebtn } from "../../common/squareBtnProps";
import { projectValidate } from "../../../utils/Validations/adminFormValidationUtil";
import { handleAddProjectDetails } from "../../../utils/FormSubmit/addProjectFormSubmit";
import useInterceptor from "../../../hooks/useApiInterceptor";
import { useLoader } from "../../../hooks/useLoader";
import { useAuth } from "../../../context/AuthContext";
import handleUpdate from "../../../utils/FormSubmit/updateProjectSubmit";
import { useNavigate } from "react-router-dom";
import { log } from "../../../utils/logger";

const FormPreviewContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  // border: 1px solid red;

  @media (max-width: 767px) {
    flex-direction: column;
  }

  .formBox {
    width: 100%;
  }

  .PreviewBox {
    width: 100%;
    .previewTittle {
      font-weight: bold;
      text-align: center;
      margin-top: 5px;
    }
  }

  @media (min-width: 768px) {
    .PreviewBox {
      border: none;
      width: 50%;
    }

    .formBox {
      width: 50%;
    }
  }
`;

interface ProjectFormProps {
  initialData?: ProjectDataProps; // fetched data
  id?: string;
  mode: "edit" | "add";
}

const initialAddProject = {
  id: uuidv4(), // Generate unique ID for each project,
  isOngoing: false,
  name: "",
  dateStarted: null,
  endDate: null,
  type: null,
  description: "",
  galleryData: {
    imgData: [],
    vidData: [],
  },
  links: {
    liveDemo: "",
    github: "",
  },
};

const ProjectForm: React.FC<ProjectFormProps> = ({ initialData, mode, id }) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false); //Form submitted state
  const { setShowLoader, LoaderComponent } = useLoader(); // Loader state
  const { showAlert, closeAlert } = useAuth(); //global
  const api = useInterceptor();
  const { fetchProjects } = useAuth();
  const navigate = useNavigate();

  //initial projectData state
  const [projectData, setProjectData] = useState<ProjectDataProps>(
    initialData ?? initialAddProject
  );

  //initial project form errors
  const [errors, setErrors] = useState<ProjectFormErrors>({
    name: "",
    dateStarted: "",
    endDate: "",
    type: "",
    description: "",
    galleryData: "",
    links: {
      liveDemo: "",
      github: "",
    },
  });

  //initial touch state
  const [touched, setTouched] = useState<ProjectTouchedFields>({
    name: false,
    dateStarted: false,
    endDate: false,
    type: false,
    description: false,
    galleryData: false,
    links: {
      liveDemo: false,
      github: false,
    },
  });

  // update the state when initialData becomes available.
  useEffect(() => {
    if (initialData) {
      setProjectData(initialData);
    }
  }, [initialData]);

  const validateWrapper = () =>
    projectValidate(projectData, errors, setErrors, setTouched);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

      //Run validation, assumes there is no data and yet "projectData" is there
      const validatedData = validateWrapper();
      log("Validated", validatedData);
      if(!validatedData){
         showAlert(
      true,
      "Validation Error",
      "Please fill in all required fields now",
      false
    );
     return; //end process
      }

      //Now we have valid data we can proceed
      log("Project form data", projectData);

    if (mode === "edit" && id) {
      await handleUpdate({
        id,
        updateData: projectData,
        setShowLoader,
        showAlert,
        closeAlert,
        api,
        fetchProjects,
        navigate,
      });
    } else {
      const success = await handleAddProjectDetails(
        projectData,
        setProjectData,
        setTouched,
        // validateWrapper,
        { showAlert, showLoader: setShowLoader },
        api,
        fetchProjects
      );
      if (success === true) {
        setIsFormSubmitted((prev) => !prev); // ✅ Toggle state to notify components
      }
    }
  };

  return (
    <>
      <FormPreviewContainer>
        <div className="formBox">
          <form onSubmit={handleSubmit}>
            {/* Project Name */}
            <CombinedInput
              projectData={projectData}
              setProjectData={setProjectData}
              errors={errors}
              setErrors={setErrors}
              touched={touched}
              setTouched={setTouched}
            />

            {/* Files */}
            <FilesInputComp
              projectData={projectData}
              setProjectData={setProjectData}
              errors={errors}
              setErrors={setErrors}
              touched={touched}
              setTouched={setTouched}
              showAlert={showAlert}
              isFormSubmitted={isFormSubmitted}
              setIsFormSubmitted={setIsFormSubmitted}
              // showLoader={setshowLoader}
            />

            {/* Links */}
            <LinksInputComp
              projectData={projectData}
              setProjectData={setProjectData}
              errors={errors}
              setErrors={setErrors}
              touched={touched}
              setTouched={setTouched}
              showAlert={showAlert}
              // showLoader={setshowLoader}
            />

            <div style={endAlignment}>
              <Squarebtn type="submit" height="50px" width="120px">
                {mode === "edit" ? "Update" : "Add"}
              </Squarebtn>
            </div>
          </form>
        </div>

        <div className="PreviewBox">
          <p className="previewTittle">Project Preview</p>
          <ProjectView project={projectData} />
        </div>
      </FormPreviewContainer>

      {/* Show Loader while submitting */}
      {LoaderComponent}
    </>
  );
};

export default ProjectForm;
